/**
 * ServicesOverview Component
 *
 * Renders the "Discover the Power of Our Services" section with a badge, heading, and a grid of service cards.
 * Features:
 * - Responsive layout with a section badge and gradient heading
 * - Four ServiceCard components, each with an icon, title, and description
 * - Uses SVG icons inline for each service
 * - Grid adapts for mobile, tablet, and desktop (max 2 columns)
 *
 * @returns {JSX.Element} The services overview section with all service cards
 */
'use client';

import React from 'react';
import ServiceCard from './ServiceCard';
import SectionBadge from './SectionBadge';

import { StrategicIcon, DesignIcon, DeliveryIcon, CloudIcon } from './icons';

export default function ServicesOverview() {
  /**
   * Array of service card data.
   * Each card includes an icon (SVG), title, and description (can include JSX for highlights).
   */
  const serviceCards = [
    {
      icon: <StrategicIcon />,
      title: 'Strategic Advisory',
      description: (
        <>
          We provide <span className="gradient-text">consulting services</span> that align your
          technology decisions with your business goals — offering clarity, direction, and expert
          insights.
        </>
      ),
    },
    {
      icon: <DesignIcon />,
      title: 'Product Design',
      description: (
        <>
          We offer a service of design thinking with{' '}
          <span className="gradient-text">UX/UI precision</span>, creating digital experiences that
          are both beautiful and functional.
        </>
      ),
    },
    {
      icon: <DeliveryIcon />,
      title: 'Delivery Leadership',
      description: (
        <>
          We <span className="gradient-text">manage your digital initiatives</span>, ensuring timely
          execution and stakeholder alignment.
        </>
      ),
    },
    {
      icon: <CloudIcon />,
      title: 'Cloud Infrastructure & Ops',
      description: (
        <>
          We offer scalable <span className="gradient-text">cloud engineering services</span>{' '}
          designed for performance, compliance, and long-term growth — from architecture to
          optimization.
        </>
      ),
    },
  ];

  return (
    <section className="w-full px-6 py-1 md:py-16 lg:py-2">
      <div className="max-w-7xl mx-auto">
        {/* Header section with badge and heading */}
        <div className="flex flex-col items-center mb-12 md:mb-20">
          <SectionBadge text="Swift and Scalable" />
          <h2 className="gradient-text font-manrope font-semibold text-3xl md:text-4xl lg:text-5xl tracking-[-0.48px] md:tracking-[-0.72px] lg:tracking-[-0.96px] leading-[40px] md:leading-[50px] lg:leading-[60px] text-center mt-4 md:mt-6">
            Discover the Power of Our Services
          </h2>
        </div>

        {/* Cards container: responsive grid for service cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 lg:gap-[90px] max-w-[956px] mx-auto">
          {serviceCards.map((card, index) => (
            <div key={index} className="flex justify-center md:justify-start">
              <ServiceCard icon={card.icon} title={card.title} description={card.description} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
